import { Component, computed, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { debounceTime } from 'rxjs/operators'
import { MapGeoJSONFeature } from 'maplibre-gl'
import { MapEventsService } from 'src/app/core/services/map-events.service'
import { AppStateService } from 'src/app/core/services/app-state.service'
import { Layers } from 'src/app/shared/layers-config/layers'
import { DetailsItemModel, DetailsModel } from 'src/app/shared/models/details.model'
import { AppPaths } from 'src/app/app-routing.module'

@Component({
  selector: 'app-non-detections-details',
  templateUrl: './non-detections-details.component.html',
  standalone: false,
})
export class NonDetectionsDetailsComponent {
  public readonly details = signal(new Map<string, DetailsModel>())
  public readonly rawFeature = computed(() =>
    JSON.stringify(this.features()[this.index()], undefined, 2),
  )
  public readonly features = signal<MapGeoJSONFeature[]>([])
  public readonly index = signal(0)
  public readonly selectedFeatureId = signal('')

  constructor(
    private readonly mapEventsService: MapEventsService,
    private readonly appState: AppStateService,
  ) {
    this.mapEventsService.featureClick
      .pipe(takeUntilDestroyed(), debounceTime(50))
      .subscribe((features) => {
        const nonDetectionFeatures = features.filter(
          (feature) => feature.layer.id === Layers.NonDetections.name,
        )

        if (nonDetectionFeatures.length === 0) {
          this.close()
          return
        }

        this.details.set(new Map())
        if (this.features().length !== nonDetectionFeatures.length) {
          this.index.set(0)
        }

        const clickedFeature = nonDetectionFeatures[this.index()]
        const clickedProperties = clickedFeature.properties as { clusterId?: string }
        const clickedClusterId = String(clickedProperties.clusterId ?? clickedFeature.id ?? '')
        if (clickedClusterId && clickedClusterId === this.selectedFeatureId()) {
          this.close()
          return
        }

        this.features.set(nonDetectionFeatures)
        this.setDetails()
      })
  }

  toggleFeature(index: number): void {
    if (!this.features()[index]) return

    this.index.set(index)
    this.setDetails()
  }

  setDetails(): void {
    const feature = this.features()[this.index()]
    if (!feature) return

    const properties = feature.properties as { clusterId?: string; count?: number }
    this.selectedFeatureId.set(String(properties.clusterId ?? feature.id ?? ''))
    const items: DetailsItemModel[] = [
      { description: 'Cluster ID', value: this.selectedFeatureId() || '-' },
      { description: 'Detection count', value: String(properties.count ?? '-') },
    ]

    const details = new Map(this.details())
    details.set('Non-Detection Cluster', {
      title: 'Non-Detection Cluster',
      items,
    })
    this.details.set(details)
    this.appState.setState(AppPaths.Internal, { selectedFeatureId: this.selectedFeatureId() })
  }

  close(): void {
    if (this.appState.getState().selectedFeatureId === this.selectedFeatureId()) {
      this.appState.setState(AppPaths.Internal, { selectedFeatureId: undefined })
    }
    this.details.set(new Map())
    this.features.set([])
    this.index.set(0)
    this.selectedFeatureId.set('')
  }
}
